import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function IncidentResponsePlan() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Incident Response Planning Services | Synthera Security</title>

        <meta
          name="description"
          content="Build a mature incident response capability with incident response planning, tabletop exercises, crisis communications planning, and cyber resilience services."
        />

        <meta
          name="keywords"
          content="Incident Response Plan, Cyber Incident Response, Tabletop Exercises, Crisis Management, Ransomware Response, Security Consulting, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="Incident Response Planning Services | Synthera Security"
        />

        <meta
          property="og:description"
          content="Prepare your organization for cyber incidents with expert-led incident response planning and response readiness services."
        />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/incident-response-plan"
        />

        <meta property="og:type" content="website" />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/incident-response-plan"
        />
      </Helmet>

      <section className="bg-[#032d28] text-white relative overflow-hidden flex flex-col items-center py-24 px-6 min-h-screen">
        {/* BACK BUTTON */}
        <Link
          to="/services/security-consulting-advisory"
          className="absolute top-20 left-8 text-white/40 hover:text-[#24ff81] transition-colors"
        >
          <ArrowLeft size={26} />
        </Link>

        <div className="max-w-3xl mx-auto text-left w-full">
          {/* IMAGE */}
          <div className="flex justify-center mb-10">
            <img
              src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1776681557/incident_R_qa327w.jpg"
              alt="incident response"
              className="w-44 h-44 rounded-full object-cover shadow-xl"
            />
          </div>
          {/* TITLE */}
          <h1 className="text-3xl md:text-4xl font-bold tracking-wide mb-6 uppercase text-center">
            Incident Response Plan
          </h1>

          {/* INTRO */}
          <p className="text-white/80 leading-relaxed mb-10">
            <b>Cyber incidents</b> are no longer a matter of if, but when. The
            difference between a minor disruption and a business wide crisis
            depends on how prepared your organisation is to respond. A
            well-designed Incident Response Plan ensures your teams act quickly,
            confidently, and in a coordinated way when every minute matters.
          </p>

          {/* BENEFITS */}
          <h2 className="text-xl font-bold mb-4">
            The <b>benefits</b> of a mature Incident Response capability are:
          </h2>

          <div className="space-y-4 text-white/80 mb-12">
            <p>
              <b>Reduced business impact</b>, as clear processes enable faster
              containment, eradication, and recovery from cyber incidents.
            </p>

            <p>
              <b>Improved decision making under pressure</b>, ensuring
              leadership, legal, IT, and communications teams know their roles
              and responsibilities during a crisis.
            </p>

            <p>
              <b>Regulatory and stakeholder confidence</b>, through structured
              response procedures aligned with legal, compliance, and reporting
              requirements.
            </p>
          </div>

          {/* APPROACH */}
          <h2 className="text-xl font-bold mb-4">Our Approach</h2>
          <p className="text-white/80 leading-relaxed mb-6">
            Effective incident response requires more than a document it
            requires a capability.
          </p>

          <p className="text-white/80 leading-relaxed mb-6">
            We work closely with your technical teams, leadership, legal, HR,
            and communications functions to design practical, actionable, and
            organisation-specific response plans.
          </p>

          <p className="text-white/80 leading-relaxed mb-12">
            Our approach combines threat modelling, business impact analysis,
            and hands-on workshops to ensure your response strategy reflects
            your real risks, technologies, and organisational structure.
          </p>

          {/* EXPERIENCE */}
          <h2 className="text-xl font-bold mb-4">Our Experience</h2>
          <p className="text-white/80 leading-relaxed mb-6">
            We have supported organisations across multiple sectors in building
            and maturing incident response capabilities that stand up to
            real-world attacks. From ransomware and insider threats to cloud
            breaches and supply chain compromises, our experience ensures your
            teams are prepared for the incidents most likely to affect your
            business.
          </p>

          <p className="text-white/80 leading-relaxed mb-12">
            Our work spans executive tabletop exercises, SOC integration, crisis
            communications planning, and alignment with global standards and
            regulatory expectations.
          </p>

          {/* KEY SERVICES */}
          <h2 className="text-xl font-bold mb-4">Key Services</h2>
          <p className="text-white/80 mb-4">
            Our most utilised Incident Response Planning services include:
          </p>

          <ul className="list-disc pl-6 space-y-3 text-white/80 mb-14">
            <li>
              <b>Incident Response strategy and roadmap</b>, defining
              governance, escalation paths, and response maturity goals.
            </li>

            <li>
              <b>Incident Response Plan</b> development, including roles,
              responsibilities, workflows, and playbooks tailored to your
              environment.
            </li>

            <li>
              <b>Scenario-based</b> playbooks, covering ransomware, phishing,
              data breaches, insider threats, cloud compromise, and business
              email compromise.
            </li>

            <li>
              <b>Crisis communications planning</b>, ensuring clear internal and
              external messaging during a cyber incident.
            </li>

            <li>
              <b>Tabletop exercises and simulations</b>, designed to test and
              refine your response processes in realistic scenarios.
            </li>

            <li>
              <b>Post-incident review frameworks</b>, helping organisations
              learn, improve, and strengthen resilience after incidents.
            </li>
          </ul>

          {/* CTA */}
          <p className="text-white/80 mb-8">
            Whether you are building your first Incident Response Plan or
            enhancing an existing capability, we can help you prepare, respond,
            and recover with confidence.
          </p>

          <div className="flex justify-center">
            <Link
              to="/contact"
              className="border border-white px-8 py-3 uppercase text-sm tracking-widest hover:bg-white hover:text-[#032d28] transition no-underline"
            >
              Find Out More
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
